import {hasFlag, isNullOrEmpty, translation} from "@2d-front-monorepo/core";
import {MvvmDataProvider, MvvmDataProviderRef, MvvmTextInput, useInitializeMvvmContext} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useMemo} from "react";
import AddressUnitDesignator from "../enums/AddressUnitDesignator";
import {PermitsMvvmErrorCollector} from "../index";
import {Company} from "../types/Company";
import {ComponentWithChildrenParts} from "./ComponentParts";

export interface CreateCompanyModel {
	arguments: Company;
}

export enum CreateCompanyFlags {
	None = 0,
	Name = 1,
	TaxId = 2,
	PostalCode = 4,
	State = 8,
	City = 16,
	Street = 32,
	StreetNumber = 64,
	Unit = 128
}

export interface CreateCompanyConfiguration {
	readOnly?: CreateCompanyFlags;
	hidden?: CreateCompanyFlags;
	required?: CreateCompanyFlags;
}

export interface CreateCompanyComponentProps extends ComponentWithChildrenParts<CreateCompanyModel> {
	configuration?: CreateCompanyConfiguration;
	initialState: CreateCompanyModel;
	contextRef?: RefObject<MvvmDataProviderRef<CreateCompanyModel>>;
	onSubmit: (formData: CreateCompanyModel) => Promise<void>;
}

export default function CreateCompanyComponent(props: CreateCompanyComponentProps): JSX.Element {
	const {
		initialState,
		configuration,
		onSubmit,
		contextRef,
		header,
		children,
		footer
	} = props;

	const readOnlyFields = configuration?.readOnly || CreateCompanyFlags.None;
	const hiddenFields = configuration?.hidden || CreateCompanyFlags.None;
	const requiredFields = configuration?.required || CreateCompanyFlags.None;

	const {context} = useInitializeMvvmContext({
		initialState: function () {
			const args = initialState.arguments;
			if (args.name == null) args.name = "";
			if (args.taxId == null) args.taxId = "";
			if (args.address == null) args.address = {};
			const address = args.address;
			if (address.streetNumber == null) address.streetNumber = "";
			if (address.street == null) address.street = "";
			if (address.city == null) address.city = "";
			if (address.state == null) address.state = "";
			if (address.postalCode == null) address.postalCode = "";
			if (address.unit == null) address.unit = {designator: AddressUnitDesignator.None, unit: ""};
			return initialState;
		},
		contextRef: contextRef
	});

	function requiredValidation(flag: CreateCompanyFlags, translationKey: string) {
		const isRequired = hasFlag(requiredFields, flag);
		return useMemo(function () {
			if (!isRequired) return undefined;
			return {
				required: function (value: string | undefined): string | undefined {
					if (isNullOrEmpty(value)) return translation(translationKey);
					return undefined;
				}
			};
		}, [isRequired]);
	}

	const nameValidation = requiredValidation(CreateCompanyFlags.Name, PermitTranslations.validation__business__name__required);
	const taxIdValidation = requiredValidation(CreateCompanyFlags.TaxId, PermitTranslations.validation__business__tax_id__required);
	const streetNumberValidation = requiredValidation(CreateCompanyFlags.StreetNumber, PermitTranslations.validation__residency__street_number__required);
	const streetValidation = requiredValidation(CreateCompanyFlags.Street, PermitTranslations.validation__residency__street__required);
	const unitValidation = requiredValidation(CreateCompanyFlags.Unit, PermitTranslations.validation__residency__unit__required);
	const postalCodeValidation = requiredValidation(CreateCompanyFlags.PostalCode, PermitTranslations.validation__residency__postal_code__required);
	const cityValidation = requiredValidation(CreateCompanyFlags.City, PermitTranslations.validation__residency__city__required);
	const stateValidation = requiredValidation(CreateCompanyFlags.State, PermitTranslations.validation__residency__state__required);

	return (
		<MvvmDataProvider<CreateCompanyModel> onSubmit={onSubmit} context={context}>
			<PermitsMvvmErrorCollector/>
			<div className="layout vertical gap">
				{header?.(context.state)}
				<div className="grid">
					<MvvmTextInput context={context}
					               property="arguments.name"
					               type="text"
					               column="col-3of4"
					               autoComplete={"organization"}
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.Name)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.Name)}
					               validation={nameValidation}
					               label={translation(PermitTranslations.new_company__name)}/>

					<MvvmTextInput context={context}
					               property="arguments.taxId"
					               type="text"
					               column="col-1of4"
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.TaxId)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.TaxId)}
					               validation={taxIdValidation}
					               label={translation(PermitTranslations.new_company__tax_id)}/>
				</div>
				<div className="grid">
					<MvvmTextInput context={context}
					               property="arguments.address.streetNumber"
					               type="text"
					               column="col-1of4"
					               autoComplete={"off"}
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.StreetNumber)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.StreetNumber)}
					               validation={streetNumberValidation}
					               label={translation(PermitTranslations.new_residency__street_number)}/>

					<MvvmTextInput context={context}
					               property="arguments.address.street"
					               type="text"
					               column="col-1of2"
					               autoComplete={"address-line1"}
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.Street)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.Street)}
					               validation={streetValidation}
					               label={translation(PermitTranslations.new_residency__street)}/>

					<MvvmTextInput context={context}
					               property="arguments.address.unit.unit"
					               type="text"
					               column="col-1of4"
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.Unit)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.Unit)}
					               validation={unitValidation}
					               label={translation(PermitTranslations.new_residency__apt_unit)}/>

					<MvvmTextInput context={context}
					               property="arguments.address.postalCode"
					               type="text"
					               column="col-1of4"
					               autoComplete="postal-code"
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.PostalCode)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.PostalCode)}
					               validation={postalCodeValidation}
					               label={translation(PermitTranslations.new_residency__postal_code)}/>

					<MvvmTextInput context={context}
					               property="arguments.address.city"
					               type="text"
					               column="col-1of4"
					               autoComplete="address-level2"
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.City)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.City)}
					               validation={cityValidation}
					               label={translation(PermitTranslations.new_residency__city)}/>

					<MvvmTextInput context={context}
					               property="arguments.address.state"
					               type="text"
					               column="col-1of4"
					               autoComplete="address-level1"
					               hidden={hasFlag(hiddenFields, CreateCompanyFlags.State)}
					               disabled={hasFlag(readOnlyFields, CreateCompanyFlags.State)}
					               validation={stateValidation}
					               label={translation(PermitTranslations.new_residency__state)}/>
				</div>
				{children}
				{footer?.(context.state)}
			</div>
		</MvvmDataProvider>
	);
}
